import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Plus, X, Loader2 } from 'lucide-react';
import { api } from '../../services/api';
import { DEFAULT_DESCRIPTION_PHRASES } from '@findmystuff/shared';
import clsx from 'clsx';

interface PhraseDropdownProps {
  onInsert: (phrase: string) => void;
  className?: string;
}

export function PhraseDropdown({ onInsert, className }: PhraseDropdownProps) {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [showAddNew, setShowAddNew] = useState(false);
  const [newPhrase, setNewPhrase] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch user's custom phrases
  const { data: customPhrases = [], isLoading } = useQuery({
    queryKey: ['phrases'],
    queryFn: api.phrases.list,
  });

  // Create phrase mutation
  const createMutation = useMutation({
    mutationFn: (phrase: string) => api.phrases.create(phrase),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['phrases'] });
      setNewPhrase('');
      setShowAddNew(false);
    },
  });

  // Delete phrase mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.phrases.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['phrases'] });
    },
  });

  // Increment usage mutation
  const usageMutation = useMutation({
    mutationFn: (id: string) => api.phrases.incrementUsage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['phrases'] });
    },
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowAddNew(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when showing add new
  useEffect(() => {
    if (showAddNew && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showAddNew]);

  const handleInsertPhrase = (phrase: string, phraseId?: string) => {
    onInsert(phrase);
    if (phraseId) {
      usageMutation.mutate(phraseId);
    }
    setIsOpen(false);
  };

  const handleAddNewPhrase = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPhrase.trim()) {
      createMutation.mutate(newPhrase.trim());
    }
  };

  const handleDeletePhrase = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (confirm('Delete this phrase?')) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className={clsx('relative', className)} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
      >
        Insert phrase
        <ChevronDown className={clsx('w-4 h-4 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <div className="absolute right-0 z-50 mt-1 w-80 sm:w-96 bg-white border border-gray-200 rounded-lg shadow-lg max-h-[70vh] overflow-hidden flex flex-col">
          {/* Custom phrases section */}
          {customPhrases.length > 0 && (
            <>
              <div className="px-3 py-2 bg-gray-50 border-b">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  My Phrases
                </span>
              </div>
              <div className="overflow-y-auto max-h-40">
                {customPhrases.map((phrase) => (
                  <div
                    key={phrase.id}
                    className="flex items-start group hover:bg-gray-50"
                  >
                    <button
                      type="button"
                      onClick={() => handleInsertPhrase(phrase.phrase, phrase.id)}
                      className="flex-1 px-3 py-2 text-left text-sm text-gray-700 break-words"
                    >
                      {phrase.phrase}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleDeletePhrase(e, phrase.id)}
                      className="p-2 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                      title="Delete phrase"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Add new phrase */}
          <div className="border-b">
            {showAddNew ? (
              <form onSubmit={handleAddNewPhrase} className="p-2">
                <div className="flex gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={newPhrase}
                    onChange={(e) => setNewPhrase(e.target.value)}
                    placeholder="Enter new phrase..."
                    className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                    maxLength={500}
                    spellCheck={true}
                    autoCorrect="on"
                    autoCapitalize="sentences"
                    autoComplete="one-time-code"
                  />
                  <button
                    type="submit"
                    disabled={!newPhrase.trim() || createMutation.isPending}
                    className="px-2 py-1 text-sm bg-primary-500 text-white rounded hover:bg-primary-600 disabled:opacity-50"
                  >
                    {createMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Add'
                    )}
                  </button>
                </div>
                {createMutation.isError && (
                  <p className="text-xs text-red-500 mt-1">
                    {(createMutation.error as Error)?.message || 'Failed to add phrase'}
                  </p>
                )}
              </form>
            ) : (
              <button
                type="button"
                onClick={() => setShowAddNew(true)}
                className="w-full px-3 py-2 text-left text-sm text-primary-600 hover:bg-gray-50 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add new phrase...
              </button>
            )}
          </div>

          {/* Default phrases section */}
          <div className="px-3 py-2 bg-gray-50 border-b">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Common Phrases
            </span>
          </div>
          <div className="overflow-y-auto flex-1">
            {isLoading ? (
              <div className="p-4 text-center text-gray-500">
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
              </div>
            ) : (
              DEFAULT_DESCRIPTION_PHRASES.map((phrase) => (
                <button
                  key={phrase}
                  type="button"
                  onClick={() => handleInsertPhrase(phrase)}
                  className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 break-words"
                >
                  {phrase}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
